import React, { useState } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { apiService, BankFilePreview } from '../services/api';

export function BankFiles() {
  const { user } = useAuth();
  const [fileName, setFileName] = useState('');
  const [xmlContent, setXmlContent] = useState('');
  const [preview, setPreview] = useState<BankFilePreview | null>(null);
  const [confirmText, setConfirmText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [success, setSuccess] = useState<string>();

  async function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setPreview(null);
    setConfirmText('');
    setError(undefined);
    setSuccess(undefined);
    setFileName(file.name);
    const content = await file.text();
    setXmlContent(content);
    setLoading(true);
    try {
      setPreview(await apiService.previewBankFile(content));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to read file');
    } finally {
      setLoading(false);
    }
  }

  async function handleSend() {
    if (!preview) return;
    setLoading(true);
    setError(undefined);
    try {
      const result = await apiService.uploadBankFile(xmlContent);
      setSuccess(result.message);
      setPreview(null);
      setXmlContent('');
      setConfirmText('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send file');
    } finally {
      setLoading(false);
    }
  }

  if (!user?.isAdmin) {
    return (
      <Layout>
        <p className="text-sm text-muted-foreground">Only admins can send bank files.</p>
      </Layout>
    );
  }

  const canSend = !!preview && !preview.duplicate && confirmText === preview.messageId && !loading;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Bank files</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Send a pain.001 XML straight to Citi (PGP + SFTP), exactly as written. No batch is created — the file is
            not validated against the payment rules, so check every field before sending.
          </p>
        </div>

        {error && (
          <div className="bg-destructive/10 border border-destructive/30 text-destructive text-sm rounded-md px-4 py-2">
            {error}
          </div>
        )}
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm rounded-md px-4 py-2">
            {success}
          </div>
        )}

        <div className="bg-white border border-border/60 rounded-xl p-6 space-y-4">
          <label className="inline-flex items-center h-9 px-4 rounded-md border border-border/60 text-sm cursor-pointer hover:bg-muted transition-colors">
            Choose XML file
            <input type="file" accept=".xml,application/xml,text/xml" className="hidden" onChange={handleFile} />
          </label>
          {fileName && <span className="ml-3 text-sm text-muted-foreground">{fileName}</span>}
          {loading && <p className="text-sm text-muted-foreground">Working…</p>}

          {preview && (
            <div className="space-y-4">
              <div className="text-sm space-y-1">
                <div>
                  <span className="text-muted-foreground">MsgId:</span>{' '}
                  <span className="font-mono">{preview.messageId}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">SFTP file:</span>{' '}
                  <span className="font-mono">{preview.filename}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Transactions:</span> {preview.numberOfTransactions}
                </div>
              </div>

              <table className="w-full text-xs border border-border/60 rounded-md">
                <thead>
                  <tr className="border-b border-border/60 bg-muted/60 text-left text-muted-foreground">
                    <th className="px-3 py-2 font-medium">PmtInfId</th>
                    <th className="px-3 py-2 font-medium">Type</th>
                    <th className="px-3 py-2 font-medium">Purpose</th>
                    <th className="px-3 py-2 font-medium">Execution date</th>
                    <th className="px-3 py-2 font-medium text-right">Transactions</th>
                    <th className="px-3 py-2 font-medium text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.paymentInfos.map((p) => (
                    <tr key={p.paymentInfoId} className="border-b border-border/60 last:border-0">
                      <td className="px-3 py-2 font-mono">{p.paymentInfoId}</td>
                      <td className="px-3 py-2">{p.localInstrument ?? '—'}</td>
                      <td className="px-3 py-2">{p.categoryPurpose ?? '—'}</td>
                      <td className="px-3 py-2">{p.executionDate}</td>
                      <td className="px-3 py-2 text-right">{p.transactions}</td>
                      <td className="px-3 py-2 text-right font-mono">
                        {Object.entries(p.totals)
                          .map(([ccy, total]) => `${total.toFixed(2)} ${ccy}`)
                          .join(', ')}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {preview.duplicate ? (
                <p className="text-sm text-destructive">
                  This MsgId was already sent. Change MsgId (and PmtInfId) before sending again.
                </p>
              ) : (
                <div className="space-y-2">
                  <p className="text-sm">
                    This sends real payments to Citi. Type <span className="font-mono">{preview.messageId}</span> to
                    confirm.
                  </p>
                  <div className="flex items-center gap-3">
                    <input
                      value={confirmText}
                      onChange={(e) => setConfirmText(e.target.value)}
                      className="h-9 w-96 px-3 rounded-md border border-border/60 text-sm font-mono"
                    />
                    <button
                      onClick={handleSend}
                      disabled={!canSend}
                      className="h-9 px-4 rounded-md bg-destructive text-white text-sm disabled:opacity-40"
                    >
                      Send to Citi
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
